// deps
import React, { ReactElement, useMemo, useState } from "react";

// context
import { UpdateCartContext, UpdateCartContextValue } from "@cart-client/contexts/UpdateCartContext";
import { CouponListingContext } from "@cart-client/contexts/CouponListingContext";

// hooks
import { usePerformCartUpdate } from "@cart-client/hooks/usePerformCartUpdate";
import useApplyCoupon from "@cart-client/hooks/useApplyCoupon";

// types
import { FlowType } from "@swiggy-private/super-ui";

export function CartActionProvider(props: { children: React.ReactNode }): ReactElement {
    const [{ updateCartInProgress, updateCartData, updateCartError }, doUpdateCart, reloadCart] =
        usePerformCartUpdate();
    const [showM2Chalfcard, setShowM2Chalfcard] = useState(false);
    const [showFreebiePopup, setShowFreebiePopup] = useState(false);

    // to render m2c halfcard on click
    const [showM2ChalfcardOnClick, setShowM2ChalfcardOnClick] = useState({
        status: false,
        flowType: FlowType.M2C,
    });

    const {
        performApplyCoupon,
        performRemoveCoupon,
        toggleCouponModal,
        showCouponModal,
        updateCouponState,
        syncApplyCouponCart,
        fetchCoupons,
        isFetching,
        isApplyingCoupon,
        coupons,
        nextBestCoupon,
        errorMessage,
        clearErrorMessage,
        resetCouponListingPage,
        togglingDelightPopup,
        showDelightPopup,
        handleCouponListingPageCouponRemove,
        isRemovingCoupon,
        tncCoupon,
        setTncCoupon,
        showPendingDiscountingComms,
        cards,
        hasInitiallyFetched,
    } = useApplyCoupon();

    const updateCartContextValue: UpdateCartContextValue = useMemo(
        () => ({
            updateCartInProgress: updateCartInProgress || updateCouponState,
            updateCartData,
            updateCartError,
            doUpdateCart,
            reloadCart,
            performApplyCoupon,
            performRemoveCoupon,
            toggleCouponModal,
            showCouponModal,
            syncApplyCouponCart,
            resetCouponListingPage,
            showDelightPopup,
            togglingDelightPopup,
            handleCouponListingPageCouponRemove,
            showM2Chalfcard,
            setShowM2Chalfcard,
            showM2ChalfcardOnClick,
            setShowM2ChalfcardOnClick,
            showPendingDiscountingComms,
            showFreebiePopup,
            setShowFreebiePopup,
        }),
        [
            updateCartInProgress,
            updateCouponState,
            updateCartData,
            updateCartError,
            doUpdateCart,
            reloadCart,
            performApplyCoupon,
            performRemoveCoupon,
            toggleCouponModal,
            showCouponModal,
            syncApplyCouponCart,
            resetCouponListingPage,
            showDelightPopup,
            togglingDelightPopup,
            handleCouponListingPageCouponRemove,
            showM2Chalfcard,
            setShowM2Chalfcard,
            showM2ChalfcardOnClick,
            setShowM2ChalfcardOnClick,
            showPendingDiscountingComms,
            showFreebiePopup,
        ],
    );

    const couponListingContextValue = useMemo(
        () => ({
            fetchCoupons,
            isFetching,
            isApplyingCoupon,
            coupons,
            nextBestCoupon,
            errorMessage,
            clearErrorMessage,
            resetCouponListingPage,
            isRemovingCoupon,
            tncCoupon,
            setTncCoupon,
            cards,
            hasInitiallyFetched,
        }),
        [
            clearErrorMessage,
            coupons,
            errorMessage,
            fetchCoupons,
            isApplyingCoupon,
            isFetching,
            nextBestCoupon,
            resetCouponListingPage,
            isRemovingCoupon,
            tncCoupon,
            setTncCoupon,
            cards,
            hasInitiallyFetched,
        ],
    );

    return (
        <UpdateCartContext.Provider value={updateCartContextValue}>
            <CouponListingContext.Provider value={couponListingContextValue}>
                {props.children}
            </CouponListingContext.Provider>
        </UpdateCartContext.Provider>
    );
}
